import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined'
import BookOutlinedIcon from '@mui/icons-material/BookOutlined'

import { Button } from '@/shared/components/ui/button'
import { cn } from '@/shared/lib/utils'
import React from 'react'
import {
  PLAYBOOK_PLACEHOLDERS,
  PromptTag,
  TOOL_DISCOVERY_PLACEHOLDERS,
} from './constants'
import type { PromptFormVariant } from './types'

interface ExampleButtonsProps {
  selectedTag: PromptTag | null
  onExampleSelect: (example: { title: string; value: string } | null) => void
  variant?: PromptFormVariant
  disabled?: boolean
  maxItems?: number
}

export const ExampleButtons: React.FC<ExampleButtonsProps> = ({
  selectedTag,
  onExampleSelect,
  variant: _variant = 'homepage',
  maxItems = 6,
  disabled,
}) => {
  const randomizedPlaybooks = React.useMemo(
    () => [...PLAYBOOK_PLACEHOLDERS].sort(() => Math.random() - 0.5),
    []
  )
  const randomizedToolDiscovery = React.useMemo(
    () => [...TOOL_DISCOVERY_PLACEHOLDERS].sort(() => Math.random() - 0.5),
    []
  )

  const TagIcon =
    selectedTag === PromptTag.PLAYBOOKS
      ? BookOutlinedIcon
      : (props) => (
          <BoltOutlinedIcon
            {...props}
            className={cn('!fill-transparent stroke-current stroke-2')}
          />
        )

  const examples = React.useMemo(() => {
    if (selectedTag === PromptTag.PLAYBOOKS) {
      return randomizedPlaybooks
    } else if (selectedTag === PromptTag.TOOL_DISCOVERY) {
      return randomizedToolDiscovery
    }
    return []
  }, [selectedTag, randomizedPlaybooks, randomizedToolDiscovery])

  if (examples.length === 0) {
    return null
  }

  return (
    <div className="mx-auto flex max-w-xl flex-wrap items-center justify-center gap-2 text-center">
      <div className="flex flex-wrap justify-center gap-2">
        {examples.slice(0, maxItems).map((example, index) => (
          <Button
            key={`${selectedTag}-${index}`}
            disabled={disabled}
            variant="pill"
            size="sm"
            className={cn(
              'h-8 rounded-3xl !px-3 !py-1 text-sm font-medium !text-mydra-black focus-visible:!outline-paper',
              'border border-[#E2E3E6] bg-[rgba(248,248,247,0.5)] text-gray-700',
              'hover:border-[#895AD7] hover:bg-[rgba(137,90,215,0.7)] hover:!text-white',
              'data-[state=on]:border-[#895AD7] data-[state=on]:bg-[rgba(137,90,215,0.7)] data-[state=on]:!text-white'
            )}
            onClick={() => onExampleSelect(example)}
          >
            <div className="flex items-center gap-1">
              <TagIcon
                sx={{
                  fontSize: 12,
                }}
              />
              {example.title}
            </div>
          </Button>
        ))}
      </div>
    </div>
  )
}
